import { useTwoUIFlights } from "@amx/ux-twoui-telemetry";
import { ColorName, Theme, useTheme } from "@amx/ux-twoui-theming";
import { Link, Stack as FluentStack } from "@fluentui/react";
import React, { CSSProperties } from "react";
import { Size, ViewportType } from "../../shared/constants";
import { useComponentLogger } from "../../shared/logging";
import { validateProps } from "../../shared/props";
import { ComponentValidator, validateComponent } from "../../shared/validation";
import { getColorFromCorrectPalette } from "../../styles/colors";
import { IconSize, mapIconBackplateSize } from "../../styles/icons";
import { ImageSize, mapImageBackplateSize, useViewport } from "../../styles/media";
import { Icon, IconProps } from "../Icon/Icon";
import { Image } from "../Image/Image";
import { BaseImageProps, BorderRadius } from "../Image/Image.types";
import { Stack, StackItem } from "../Stacks/Stack/Stack";
import { TruncatedText } from "../TruncatedText/TruncatedText";
import { getMediaLinkStyles, getStackForIconBackplateStyle, getStackForIconStyle, getStackForImageBackplateStyle, getStylesForTextHost } from "./Media.styles";
import { MediaCharacter, MediaFaviconSrc, MediaIcon, MediaImageCard, MediaImageSrc, MediaProps, TextHost } from "./Media.types";

const mediaSizeValidator = (character: string): ComponentValidator => (
    () => (character.length !== 1 ? "Prop 'character' must be one character" : undefined)
);
const isLowerMediaImageHeightFlightName = "lowerMediaImageHeight";

export const Media: React.FC<MediaProps> = ({
    size,
    image,
    height,
    padding,
    imagePadding,
    disabled,
    topAlign,
    borderRadius,
    horizontal = true,
    linkProps,
    children,
    ...otherProps
}) => {
    useComponentLogger("Media", otherProps.id);

    validateProps("Media", otherProps);

    const theme = useTheme();
    const viewportType = useViewport();
    let renderImageStyle: CSSProperties = {};
    let stackItemImageHeight = height || "44px";
    let stackPadding = padding || "6px 0";
    let iconSize: IconSize;
    let imageSize: ImageSize;
    const twoUIFlights = useTwoUIFlights();

    switch (size) {
        case "xs":
            imageSize = "16px";
            renderImageStyle = {
                height: imageSize,
                width: imageSize,
                lineHeight: "14px",
                fontSize: "10px"
            };
            iconSize = "12px";
            break;

        case "sm":
            imageSize = "24px";
            renderImageStyle = {
                height: imageSize,
                width: imageSize,
                lineHeight: "22px",
                fontSize: "15px"
            };
            iconSize = "16px";
            break;

        case "md":
            imageSize = "32px";
            renderImageStyle = {
                height: imageSize,
                width: imageSize,
                lineHeight: "29px",
                fontSize: "20px"
            };
            iconSize = "24px";
            break;

        case "lg":
            iconSize = "32px";
            imageSize = "48px";
            stackItemImageHeight = height ?? ((image.imageType === "icon" && image.backplate) ? mapIconBackplateSize[iconSize] : imageSize);
            renderImageStyle = {
                height: imageSize,
                width: imageSize,
                lineHeight: "45px",
                fontSize: "30px"
            };
            break;

        case "xl":
            stackPadding = padding || "10px 0";
            iconSize = "48px";
            imageSize = "60px";
            stackItemImageHeight = height ?? ((image.imageType === "icon" && image.backplate) ? mapIconBackplateSize[iconSize] : imageSize);
            renderImageStyle = {
                height: imageSize,
                width: imageSize,
                lineHeight: "57px",
                fontSize: "60px"
            };
            break;

        case "xxl":
            stackPadding = padding || "10px 0";
            iconSize = "60px";
            imageSize = "72px";
            stackItemImageHeight = height ?? ((image.imageType === "icon" && image.backplate) ? mapIconBackplateSize[iconSize] : imageSize);
            renderImageStyle = {
                height: imageSize,
                width: imageSize,
                lineHeight: "69px",
                fontSize: "45px"
            };
            break;

        case "xxxl":
            stackPadding = padding || "10px 0";
            iconSize = "96px";
            imageSize = "96px";
            stackItemImageHeight = height ?? ((image.imageType === "icon" && image.backplate) ? mapIconBackplateSize[iconSize] : imageSize);
            renderImageStyle = {
                height: imageSize,
                width: imageSize,
                lineHeight: "93px",
                fontSize: "60px"
            };
            break;
    }
    if (image.imageType === "imageCard") {
        if (twoUIFlights.includes(isLowerMediaImageHeightFlightName)) {
            imageSize = "220px";
            renderImageStyle = {
                height: "220px",
                width: "146.67px",
            };
        } else {
            switch (size) {
                case "xl":
                case "xxl":
                case "xxxl":
                case "lg":
                case "md":
                    imageSize = "246px";
                    renderImageStyle = {
                        height: "246px",
                        width: "164px",
                    };
                    break;
                case "sm":
                case "xs":
                    imageSize = "186px";
                    renderImageStyle = {
                        height: "186px",
                        width: "124px",
                    };
                    break;
            }
        }
        stackPadding = padding || "0px";
        stackItemImageHeight = height ?? imageSize;
    }

    const imageElement = () => {
        switch (image.imageType) {
            case "image":
            case "imageCard":
                return onRenderImage({
                    ...image,
                    theme,
                    viewportType,
                    style: renderImageStyle,
                    imageSize,
                    disabled,
                    borderRadius
                });
            case "imageFavicon":
                const globeIcon: Pick<IconProps, "iconName" | "color"> = image.iconProps ?? {
                    iconName: "Globe",
                    color: "neutralPrimary"
                };
                return onRenderImage({
                    ...image,
                    theme,
                    viewportType,
                    style: renderImageStyle,
                    imageSize,
                    disabled,
                    borderRadius,
                    fallbackIconProps: globeIcon
                });
            case "icon":
                return onRenderIcon({
                    theme,
                    iconSize,
                    disabled,
                    borderRadius,
                    ...image,
                });
            case "character":
                return onRenderCharacter({
                    theme,
                    style: renderImageStyle,
                    size,
                    disabled,
                    borderRadiusStyle: borderRadius,
                    ...image
                });
        }
    };

    const imageAlignment = horizontal ? "start" : "center";
    const textAlignment = horizontal ? undefined : "center";
    const stackItemPosition = image.imageType === "imageCard" && image.textHost ? "relative" : undefined;

    const stackItemInnerStyle: CSSProperties = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        height: "100%",
        position: stackItemPosition
    };

    const mediaComponent = (
        <Stack horizontal={horizontal} childrenGap="8px" padding={stackPadding} {...otherProps}>
            <StackItem align={imageAlignment} height={topAlign ? undefined : stackItemImageHeight} shrink={0} padding={imagePadding}>
                <div style={stackItemInnerStyle}>
                    {imageElement()}
                </div>
            </StackItem>
            {children && (
                <StackItem align="center" grow={true} textAlignment={textAlignment}>
                    {children}
                </StackItem>
            )}
        </Stack>
    );

    if (linkProps) {
        const { kind, ...otherLinkProps } = linkProps;

        return (
            <Link
                {...otherLinkProps}
                styles={getMediaLinkStyles()}
                disabled={disabled}
            >
                {mediaComponent}
            </Link>
        );
    }

    return mediaComponent;
};

interface OnRenderImageParams extends BaseImageProps {
    readonly theme: Theme;
    readonly viewportType: ViewportType;
    readonly imageType: MediaImageSrc["imageType"] | MediaFaviconSrc["imageType"] | MediaImageCard["imageType"];
    readonly style: CSSProperties;
    readonly imageSize: ImageSize;
    readonly backplate?: MediaImageSrc["backplate"];
    readonly textHost?: MediaImageCard["textHost"];
}
const onRenderImage = ({
    theme,
    viewportType,
    imageSize,
    backplate,
    textHost,
    imageType,
    style,
    ...imageProps
}: OnRenderImageParams) => {

    const image = textHost ?
        (
            <>
                <Image {...imageProps} alt="" height={style.height} width={style.width} />
                {onRenderTextHost(textHost, theme, imageProps.borderRadius)}
            </>
        )
        : <Image {...imageProps} alt="" height={style.height} width={style.width} />;

    if (backplate) {
        const computedBackplateColor = getColorFromCorrectPalette(theme, backplate === true ? "themePrimary" as ColorName : backplate as ColorName) ?? backplate;
        return (
            <FluentStack
                verticalAlign="center"
                horizontalAlign="center"
                styles={getStackForImageBackplateStyle(mapImageBackplateSize[imageSize], computedBackplateColor, imageProps.borderRadius)}
            >
                {image}
            </FluentStack>
        );
    }

    return image;
};

const onRenderTextHost = (textHost: TextHost, theme: Theme, borderRadius?: BorderRadius) => {
    return (
        <div style={getStylesForTextHost(textHost, theme, borderRadius)}>
            <TruncatedText  {...textHost.truncatedTextProps} />
        </div >
    );
};

interface OnRenderIconParams extends MediaIcon {
    readonly theme: Theme;
    readonly iconSize: IconSize;
    readonly disabled?: boolean;
    readonly borderRadius?: BorderRadius;
}

const onRenderIcon = ({
    theme,
    iconSize,
    borderRadius,
    imageType,
    iconColor,
    backplate,
    ...iconProps
}: OnRenderIconParams) => {
    const icon = <Icon {...iconProps} size={iconSize} color={iconColor} />;

    if (backplate) {
        const computedBackplateColor = getColorFromCorrectPalette(theme, backplate === true ? "themePrimary" : backplate);
        return (
            <FluentStack horizontal={true} styles={getStackForIconBackplateStyle(mapIconBackplateSize[iconSize], computedBackplateColor, borderRadius)}>
                <FluentStack verticalAlign="center" styles={getStackForIconStyle(mapIconBackplateSize[iconSize])}>
                    {icon}
                </FluentStack>
            </FluentStack>
        );
    }

    return icon;
};

interface OnRenderChacterParams extends MediaCharacter {
    readonly theme: Theme;
    readonly style: CSSProperties;
    readonly size: Size;
    readonly disabled?: boolean;
    readonly borderRadiusStyle?: BorderRadius;
    readonly characterVariant?: "neutralHigh" | "neutralDark";
    readonly backplateColor?: ColorName;
}

const onRenderCharacter = ({
    theme,
    style,
    size,
    disabled,
    borderRadiusStyle,
    characterVariant,
    backplateColor,
    ...characterProps
}: OnRenderChacterParams) => {
    validateComponent("Media", [mediaSizeValidator(characterProps.character)]);

    const palette = theme.palette;

    const borderRadius = borderRadiusStyle ?? (["xs", "sm", "md"].includes(size) ? "2px" : "4px");

    let background;
    let textColor;

    if (backplateColor) {
        background = getColorFromCorrectPalette(theme, backplateColor);
        // set to default if backplateColor is set, but not characterVariant
        textColor = characterVariant && characterVariant === "neutralDark" ? theme.palette.neutralDark : theme.palette.neutralHigh;
    } else {
        background = disabled ? palette.neutralLighter : palette.neutralSecondary;
        textColor = theme.palette.neutralHigh;
    }

    const letterStyle: CSSProperties = {
        background,
        color: textColor,
        borderRadius,
        ...style
    };

    return (
        <div style={letterStyle}>{characterProps.character}</div>
    );
};
